const childrenLoaded = require('@alexspirgel/children-loaded');

// Static counter for unique instance IDs
let instanceCounter = 0;

class BlockAnchorLinks extends HTMLElement {

	constructor() {
		super();
		this.instanceId = ++instanceCounter;
		this.sentinel = null;
		this.observer = null;
		this.isInNarrowContainer = false;
		this.resizeHandler = null;
	}

	async connectedCallback() {
		await childrenLoaded(this);
		for (const linkElement of this.linkElements) {
			linkElement.addEventListener('click', this.linkClickHandler);
		}
		this.initStickyObserver();
		this.checkNarrowContainer();
	}

	checkNarrowContainer() {
		this.isInNarrowContainer = !!this.closest('.narrow-block-area, .block-block-area-narrow');
		// Only calculate offset for sticky blocks in narrow containers
		if (this.isInNarrowContainer && this.isSticky) {
			this.updateContainerOffset();
			this.resizeHandler = () => this.updateContainerOffset();
			window.addEventListener('resize', this.resizeHandler);
		}
	}

	updateContainerOffset() {
		// Use the sentinel's position since it always stays at the natural document flow position
		// If sentinel doesn't exist yet, use the block's position (before it becomes stuck)
		const referenceElement = this.sentinel || this;
		const rect = referenceElement.getBoundingClientRect();
		this.style.setProperty('--anchor-links-container-offset', `${rect.left}px`);
	}

	disconnectedCallback() {
		this.destroyStickyObserver();
		if (this.resizeHandler) {
			window.removeEventListener('resize', this.resizeHandler);
			this.resizeHandler = null;
		}
	}

	destroyStickyObserver() {
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
		if (this.sentinel) {
			this.sentinel.remove();
			this.sentinel = null;
		}
		this.classList.remove('is-stuck');
	}

	get isSticky() {
		return this.classList.contains('block-anchor-links--sticky');
	}

	initStickyObserver() {
		if (!this.isSticky) {
			return;
		}

		// Clean up any existing observer before creating a new one
		this.destroyStickyObserver();

		// Create sentinel element positioned at the block's natural position
		this.sentinel = document.createElement('div');
		this.sentinel.className = 'block-anchor-links__sentinel';
		this.sentinel.id = `block-anchor-links-sentinel-${this.instanceId}`;
		this.sentinel.setAttribute('aria-hidden', 'true');
		this.sentinel.dataset.anchorLinksInstance = this.instanceId;
		this.parentNode.insertBefore(this.sentinel, this);

		// Get the top offset from CSS custom properties
		const getTopOffset = () => {
			const styles = getComputedStyle(this);
			const top = styles.getPropertyValue('top');
			return top ? parseFloat(top) : 0;
		};

		// Store reference to this block for the callback
		const block = this;

		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					// Verify this entry is for our sentinel
					if (entry.target === block.sentinel) {
						block.classList.toggle('is-stuck', !entry.isIntersecting);
					}
				});
			},
			{
				threshold: 0,
				rootMargin: `-${getTopOffset() + 1}px 0px 0px 0px`,
			}
		);

		this.observer.observe(this.sentinel);
	}

	get linkElements() {
		return this.querySelectorAll('.block-anchor-links__link');
	}

	linkClickHandler = (event) => {
		event.preventDefault();
		const linkElement = event.target;
		if (linkElement) {
			const anchorId = linkElement.getAttribute('href');
			if (anchorId) {
				history.pushState({}, '', anchorId);
				const anchorElement = document.querySelector(anchorId);
				if (anchorElement) {
					anchorElement.scrollIntoView({behavior:'smooth'});
				}
			}
		}
		return false;
	}

}

customElements.define('block-anchor-links', BlockAnchorLinks, {extends: 'section'});